import { type ConditionNode, foldConditionTree, type RelationArms } from './foldConditionTree';

const PRUNED = Symbol('pruned');

// gloss
export const pruneConditionClauses = (tree: unknown, isPruned: (node: ConditionNode) => boolean): unknown => {
  const pruneRelation = ({ node, foldInner, foldFilter }: RelationArms<unknown>): unknown => {
    if (isPruned(node)) return PRUNED;
    const condition = node.condition === undefined ? undefined : foldInner();
    const filter = node.filter === undefined ? undefined : foldFilter();
    if (condition === PRUNED || filter === PRUNED) return PRUNED;
    if (condition === node.condition && filter === node.filter) return node;
    return {
      ...node,
      ...(node.condition === undefined ? {} : { condition }),
      ...(node.filter === undefined ? {} : { filter }),
    };
  };

  const pruned = foldConditionTree<unknown>(tree, {
    literal: (value) => value,
    group: (children, { key, node }) => {
      const kept = children.filter((child) => child !== PRUNED);
      if (kept.length === children.length) {
        const original = node[key] as unknown[];
        return kept.every((child, index) => child === original[index]) ? node : { ...node, [key]: kept };
      }
      return kept.length === 0 ? PRUNED : { ...node, [key]: kept };
    },
    ifThenElse: (branches, { node }) => {
      const otherwise = branches.else === PRUNED ? undefined : branches.else;
      if (branches.if === PRUNED) {
        return branches.then === PRUNED || otherwise === undefined ? PRUNED : { any: [branches.then, otherwise] };
      }
      if (branches.then === PRUNED && otherwise === undefined) return PRUNED;
      if (branches.if === node.if && branches.then === node.then && branches.else === node.else) return node;
      const { else: _else, ...rest } = node;
      return {
        ...rest,
        if: branches.if,
        then: branches.then === PRUNED ? { all: [] } : branches.then,
        ...(otherwise === undefined ? {} : { else: otherwise }),
      };
    },
    relation: pruneRelation,
    aggregate: pruneRelation,
    leaf: ({ node }) => (isPruned(node) ? PRUNED : node),
  });

  return pruned === PRUNED ? null : pruned;
};
